import { Fragment } from 'react';

import { Link, useLocation } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { menuConfig } from '@/config/menu.config';
import { findMenuItemWithParent, getMenuLabel } from '@/utils/menu.utils';

interface Crumb {
  label: string;
  path: string | null;
}

const HOME_PATH = '/';

const getCrumbs = (pathname: string, t: TFunction): Crumb[] => {
  const home: Crumb = { label: t('header.menu.home'), path: HOME_PATH };

  if (pathname === HOME_PATH) {
    return [{ ...home, path: null }];
  }

  // Libellé non sensible : on ignore l'état d'authentification (la sidebar reste filtrée).
  const found = findMenuItemWithParent(menuConfig.items, pathname, true);
  if (!found) {
    return [home, { label: t('error.404.title'), path: null }];
  }

  const { item, parent } = found;
  return [
    home,
    ...(parent ? [{ label: getMenuLabel(parent, t), path: parent.path ?? null }] : []),
    { label: getMenuLabel(item, t), path: null },
  ];
};

/** Fil d'Ariane de l'en-tête : Accueil, rubrique parente du menu, page courante. */
export const AppBreadcrumb = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const crumbs = getCrumbs(pathname, t);

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <Fragment key={`${crumb.path ?? 'current'}-${crumb.label}`}>
              <BreadcrumbItem className={index === 0 && !isLast ? 'hidden md:inline-flex' : undefined}>
                {isLast || !crumb.path ? (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.path}>{crumb.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast ? (
                <BreadcrumbSeparator className={index === 0 ? 'hidden md:inline-flex' : undefined} />
              ) : null}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
